"use client";

/**
 * Dialog shell for a config-driven lead form whose `form_type` is `popup`.
 *
 * Mirrors `widgets/leaddrop/leaddrop1/index.tsx`: the dialog contributes only the overlay
 * and the portal, and hands ALL sizing to the form itself. That is why `DialogContent`
 * carries `p-0 border-0 bg-transparent shadow-none max-w-none` — the skin's `popupShell`
 * owns the width, radius, padding and background, exactly as `Leaddrop1` owns its own.
 * Letting the dialog impose a max-width here is what produces the classic bug of a form
 * squeezed inside a second, invisible card.
 *
 * The close affordance is the skin's too (`popupClose`), so the dialog is told to draw NONE.
 * Every other `closeBtnType` value picks WHICH × to render, not whether to — passing one of
 * those (as this did) put the dialog's × on top of the skin's in the same corner.
 */

import { Dialog, DialogContent } from "@/components/atoms/Dialog";
import { CLOSE_BTN_CATEGORY } from "@/types/common";
import { cn } from "@/lib/utils";
import ConfigLeadForm from "./ConfigLeadForm";
import type { PublicLeadFormConfig } from "@/types/leadForm";

export interface ConfigLeadFormPopupProps {
  config: PublicLeadFormConfig;
  universityId: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  context?: Record<string, unknown>;
  /** How the popup was opened — `download` makes the ending a brochure screen. */
  variant?: string;
  /** The CTA's brochure link. A course picked inside the form overrides it. */
  downloadLink?: string;
}

const ConfigLeadFormPopup = ({
  config,
  universityId,
  open,
  onOpenChange,
  onClose,
  context,
  variant,
  downloadLink,
}: ConfigLeadFormPopupProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      // The skin draws its own × (LeadFormShell → c.popupClose); the dialog must draw none.
      closeBtnType={CLOSE_BTN_CATEGORY.NONE}
      className={cn(
        "border-0 bg-transparent p-0 shadow-none",
        /**
         * ONE width utility and ONE max-width utility.
         *
         * `max-w-none` was here too, and DialogContent's base sets no max-width at all — so
         * it overrode nothing and merely raced the cap below, with the winner decided by
         * stylesheet order. `w-fit` DOES have something to override: the base is `w-full`.
         *
         * `w-fit` rather than a hardcoded artboard width because the SKIN owns the width
         * here — 320/620/908 for variant 8's sheet, 360–480 for variant 9's card. It is
         * safe only because every skin shell carries an explicit width and `shrink-0`;
         * without those it collapses to the content, which is what shrank lead popup 8's
         * sheet to ~255px on its thank-you (see leaddrop8/index.tsx).
         *
         * Keeping the dialog at the card's size also keeps the backdrop clickable: a
         * full-width transparent DialogContent would swallow every outside click.
         */
        "w-fit max-w-[calc(100vw-2rem)]",
      )}
      dialogOverlayStyle="bg-black/50"
    >
      <ConfigLeadForm
        configId={config.id}
        universityId={universityId}
        schema={config.field_schema}
        skinId={config.template_key}
        surface="popup"
        copyConfig={config.copy_config}
        themeConfig={config.theme_config}
        context={context}
        variant={variant}
        downloadLink={downloadLink}
        onClose={onClose}
      />
    </DialogContent>
  </Dialog>
);

export default ConfigLeadFormPopup;
